import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import GVarLoader from "../../ui/GVarLoader";
import {
  fetchSimBreakdown,
  fetchSimBreakdownComparison,
  fetchSimFactors,
  fetchAvailableDates,
  fetchAvailableSims,
} from "../../../services/RiskMonitorAxios";
import { FactorPanel } from "./FactorPanel";

function formatCurrency(value) {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatFullNumber(value) {
  if (value == null || isNaN(value)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function HeaderStat({ label, value, color }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: color || "text.primary", fontFamily: color ? "monospace" : "inherit" }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionHeader({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "0.68rem",
        fontWeight: 700,
        color: "#006842",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        px: 1.5,
        py: 0.75,
        bgcolor: "rgba(0,104,66,0.06)",
        borderBottom: "2px solid #006842",
        borderRadius: "6px 6px 0 0",
      }}
    >
      {children}
    </Typography>
  );
}

/** Thin magnitude bar rendered under a numeric cell's value, scaled to the row's max abs value. */
function MagnitudeBar({ value, max, color }) {
  const pct = value != null && max > 0 ? Math.min(100, (Math.abs(value) / max) * 100) : 0;
  return (
    <Box sx={{ height: 3, borderRadius: 1, bgcolor: "rgba(0,0,0,0.06)", mt: 0.4, overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: color, ml: "auto", borderRadius: 1, transition: "width 0.2s ease" }} />
    </Box>
  );
}

export default function StrategyBreakdownModal({
  open,
  onClose,
  date,
  simNumber,
  percentile,
  portfolio,
  strategyLabel,
  compareDate,
  compareSimNumber,
  source = "simulation",
}) {
  const navigate = useNavigate();
  // Portfolio greeks sims have no factor vector in the sim-factors dataset.
  const isGreeks = source === "greeks";
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState([]);
  const [error, setError] = useState(null);
  const [factors, setFactors] = useState(null);
  const [compareFactors, setCompareFactors] = useState(null);

  // Day-on-day comparison starts from whatever the caller auto-derived
  // (previous available date, same sim_number), but the user can override it
  // here to any date/sim — same editable comparison UI as the sim-details
  // (investments) page. `compareCleared` lets them drop the comparison
  // entirely even when the caller passed one in.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualCompareDate, setManualCompareDate] = useState(null);
  const [manualCompareSim, setManualCompareSim] = useState(null);
  const [compareCleared, setCompareCleared] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSims, setAvailableSims] = useState([]);

  // Reset manual overrides whenever the modal is (re)opened for a different sim.
  useEffect(() => {
    if (!open) return;
    setPickerOpen(false);
    setManualCompareDate(null);
    setManualCompareSim(null);
    setCompareCleared(false);
  }, [open, date, simNumber]);

  useEffect(() => {
    if (!pickerOpen || availableDates.length > 0) return;
    fetchAvailableDates()
      .then((dates) => setAvailableDates([...dates].sort().reverse()))
      .catch((err) => console.error("Failed to fetch available dates:", err));
  }, [pickerOpen, availableDates.length]);

  const pickerDate = manualCompareDate ?? compareDate ?? null;

  useEffect(() => {
    if (!pickerOpen || !pickerDate) return;
    let cancelled = false;
    fetchAvailableSims(pickerDate)
      .then((res) => {
        if (cancelled) return;
        const sims = res.sims || [];
        setAvailableSims(sims);
        // Default the comparison to the same sim number as the date currently
        // being viewed, when it exists on the picked comparison date too.
        if (manualCompareSim == null) {
          const match = sims.find((s) => s.sim_number === Number(simNumber));
          if (match) setManualCompareSim(match);
        }
      })
      .catch((err) => console.error("Failed to fetch available sims:", err));
    return () => { cancelled = true; };
    // Intentionally omit manualCompareSim — this only runs on pickerDate change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen, pickerDate, simNumber]);

  const effectiveCompareDate = compareCleared ? null : (manualCompareDate ?? compareDate ?? null);
  const effectiveCompareSimNumber = compareCleared
    ? null
    : (manualCompareSim != null
        ? (typeof manualCompareSim === "object" ? manualCompareSim.sim_number : manualCompareSim)
        : compareSimNumber ?? null);
  const hasComparison = effectiveCompareDate != null && effectiveCompareSimNumber != null;

  // Only worth showing a second factor panel when the comparison is actually
  // against a different simulation (a different date with the same sim
  // number shares the same shock/factor vector, so there's nothing new to show).
  const compareSimDiffers = hasComparison && Number(effectiveCompareSimNumber) !== Number(simNumber);

  useEffect(() => {
    if (!open || simNumber == null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setFactors(null);
    setCompareFactors(null);

    const breakdownPromise = hasComparison
      ? fetchSimBreakdownComparison(
          date,
          simNumber,
          effectiveCompareDate,
          effectiveCompareSimNumber,
          portfolio,
          { signal: controller.signal },
        )
      : fetchSimBreakdown(date, simNumber, portfolio, { signal: controller.signal, source });

    breakdownPromise
      .then((result) => {
        if (controller.signal.aborted) return;
        setBreakdown(result?.data || []);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err.message || "Failed to load breakdown");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    if (!isGreeks) {
      fetchSimFactors(date, simNumber, { signal: controller.signal })
        .then((result) => {
          if (controller.signal.aborted) return;
          const f = result?.factors;
          setFactors(f && Object.keys(f).length > 0 ? f : null);
        })
        .catch(() => {});

      if (compareSimDiffers) {
        fetchSimFactors(effectiveCompareDate, effectiveCompareSimNumber, { signal: controller.signal })
          .then((result) => {
            if (controller.signal.aborted) return;
            const f = result?.factors;
            setCompareFactors(f && Object.keys(f).length > 0 ? f : null);
          })
          .catch(() => {});
      }
    }

    return () => { controller.abort(); };
  }, [open, date, simNumber, portfolio, hasComparison, compareSimDiffers, effectiveCompareDate, effectiveCompareSimNumber, source, isGreeks]);

  const handleStrategyClick = (strategyTypeName) => {
    const params = new URLSearchParams({
      date,
      sim: simNumber,
      strategy: strategyTypeName,
    });
    if (portfolio) params.set("portfolio", portfolio);
    if (isGreeks) params.set("source", source);
    navigate(`/risk-monitor/investments?${params.toString()}`);
    onClose();
  };

  const totalPnl = breakdown.reduce((sum, r) => sum + (r.pnl || 0), 0);
  const totalPnlDiff = hasComparison
    ? breakdown.reduce((sum, r) => sum + (r.pnl_diff || 0), 0)
    : null;
  const maxAbsPnl = Math.max(1, ...breakdown.map((r) => Math.abs(r.pnl || 0)));
  const maxAbsDelta = Math.max(1, ...breakdown.map((r) => Math.abs(r.pnl_diff || 0)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "85vh",
          overflow: "hidden",
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        },
      }}
    >
      {/* Header — white card style with green accent stripe */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          bgcolor: "background.paper",
          py: 2,
          px: 2.5,
          borderBottom: "1px solid",
          borderColor: "grey.100",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "3px",
            background: "#006842",
          },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: "text.primary", letterSpacing: "-0.01em" }}>
              Strategy Breakdown
            </Typography>
            {percentile && (
              <Chip
                label={percentile.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: "rgba(0,104,66,0.08)",
                  color: "#006842",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  height: 20,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.25, flexWrap: "wrap" }}>
            <HeaderStat label="Sim #" value={simNumber} />
            <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.200" }} />
            <HeaderStat label="Date" value={date} />
            {strategyLabel && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.200" }} />
                <HeaderStat label="Strategy" value={strategyLabel} />
              </>
            )}
            {hasComparison && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.200" }} />
                <HeaderStat label="vs" value={`${effectiveCompareDate} · sim ${effectiveCompareSimNumber}`} />
              </>
            )}
            <Tooltip title={hasComparison ? "Change comparison" : "Compare against another date/sim"}>
              <IconButton
                size="small"
                onClick={() => setPickerOpen((v) => !v)}
                sx={{ color: pickerOpen ? "#006842" : "text.disabled" }}
              >
                {hasComparison ? <EditIcon fontSize="small" /> : <CompareArrowsIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {hasComparison && (
              <Tooltip title="Exit comparison">
                <IconButton
                  size="small"
                  onClick={() => {
                    setPickerOpen(false);
                    setManualCompareDate(null);
                    setManualCompareSim(null);
                    setCompareCleared(true);
                  }}
                  sx={{ color: "text.disabled", "&:hover": { color: "#c53030" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }} />
            <HeaderStat label="Total PnL" value={formatCurrency(totalPnl)} color={totalPnl < 0 ? "#c53030" : "#006842"} />
            {hasComparison && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: "grey.200" }} />
                <HeaderStat
                  label="Δ Total PnL"
                  value={`${totalPnlDiff < 0 ? "-" : "+"}${formatCurrency(Math.abs(totalPnlDiff))}`}
                  color={totalPnlDiff < 0 ? "#c53030" : "#006842"}
                />
              </>
            )}
          </Box>

          {pickerOpen && (
            <Box
              sx={{
                mt: 1.25,
                px: 1.5,
                py: 1,
                bgcolor: "#f4f8f6",
                borderRadius: 1.5,
                border: "1px solid rgba(0,104,66,0.2)",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <FormControl
                size="small"
                sx={{
                  minWidth: 100,
                  width: { xs: "100%", sm: 110 },
                  "& .MuiInputLabel-root": { fontSize: "0.975rem" },
                  "& .MuiSelect-select": { fontSize: "0.78rem" },
                }}
              >
                <InputLabel>Date</InputLabel>
                <Select
                  value={pickerDate || ""}
                  label="Date"
                  MenuProps={{ PaperProps: { sx: { "& .MuiMenuItem-root": { fontSize: "0.78rem" } } } }}
                  onChange={(e) => {
                    setManualCompareDate(e.target.value);
                    setManualCompareSim(null);
                    setAvailableSims([]);
                  }}
                >
                  {availableDates.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                key={pickerDate}
                size="small"
                sx={{
                  minWidth: 180,
                  flex: { sm: "1 1 220px" },
                  maxWidth: 320,
                  "& .MuiInputLabel-root": { fontSize: "0.975rem" },
                  "& .MuiAutocomplete-input": { fontSize: "0.78rem" },
                }}
                ListboxProps={{ sx: { fontSize: "0.78rem" } }}
                disabled={!pickerDate}
                value={manualCompareSim ?? (effectiveCompareSimNumber != null ? { sim_number: effectiveCompareSimNumber, label: null } : null)}
                onChange={(_, newValue) => {
                  if (typeof newValue === "string") {
                    const num = Number(newValue);
                    if (!Number.isNaN(num)) {
                      const match = availableSims.find((s) => s.sim_number === num);
                      setManualCompareSim(match || { sim_number: num, label: null, scenario_type: "mc" });
                    } else {
                      setManualCompareSim(null);
                    }
                  } else {
                    setManualCompareSim(newValue);
                  }
                }}
                options={availableSims}
                getOptionLabel={(opt) => {
                  if (opt == null) return "";
                  if (typeof opt === "number" || typeof opt === "string") return String(opt);
                  return opt.label ? `${opt.sim_number} — ${opt.label}` : String(opt.sim_number);
                }}
                isOptionEqualToValue={(opt, val) => {
                  const a = typeof opt === "object" ? opt.sim_number : opt;
                  const b = typeof val === "object" ? val?.sim_number : val;
                  return a === b;
                }}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  if (!q) return options;
                  return options.filter((s) => {
                    const num = String(s.sim_number);
                    const label = (s.label || "").toLowerCase();
                    return num.includes(q) || label.includes(q);
                  });
                }}
                freeSolo
                autoHighlight
                clearOnBlur={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sim"
                    placeholder="Search…"
                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontSize: "0.975rem" } }}
                  />
                )}
              />

              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setCompareCleared(false);
                  setPickerOpen(false);
                }}
                disabled={
                  !pickerDate
                  // A date change clears manualCompareSim — require an explicit
                  // sim pick for the new date rather than silently reusing the
                  // old (possibly invalid-for-this-date) compareSimNumber.
                  || (manualCompareSim == null && (manualCompareDate != null || effectiveCompareSimNumber == null))
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.78rem",
                  flexShrink: 0,
                  bgcolor: "#006842",
                  "&:hover": { bgcolor: "#005537" },
                }}
              >
                Apply
              </Button>
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.disabled", mt: 0.25, "&:hover": { color: "text.primary" } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: "#f8f9fa" }}>
        {loading ? (
          <GVarLoader size={120} sx={{ py: 6 }} />
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error" sx={{ fontSize: "0.85rem" }}>{error}</Typography>
          </Box>
        ) : breakdown.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary" sx={{ fontSize: "0.85rem" }}>No breakdown data available</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: factors ? { xs: "1fr", md: "300px 1fr" } : "1fr",
              gap: 1.5,
              alignItems: "start",
              p: 1.5,
            }}
          >
            {factors && (
              <FactorPanel
                factors={factors}
                compareFactors={compareFactors}
                columnLabel={compareFactors ? `Sim #${simNumber}` : null}
                compareColumnLabel={compareFactors ? `Sim #${effectiveCompareSimNumber}` : null}
              />
            )}
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
              overflow: "hidden",
            }}
          >
            <SectionHeader>Strategy PnL</SectionHeader>
            {/* Table header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 0.6,
                bgcolor: "rgba(0,104,66,0.03)",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Strategy
                </Typography>
              </Box>
              <Box sx={{ width: 110, textAlign: "right" }}>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  PnL
                </Typography>
              </Box>
              <Box sx={{ width: 60, textAlign: "right" }}>
                <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  % Total
                </Typography>
              </Box>
              {hasComparison && (
                <Box sx={{ width: 110, textAlign: "right" }}>
                  <Typography sx={{ fontSize: "0.62rem", fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Δ PnL
                  </Typography>
                </Box>
              )}
              <Box sx={{ width: 32 }} />
            </Box>

            {/* Strategy rows */}
            {breakdown.map((row, idx) => {
              const pctOfTotal =
                row.pnl != null && totalPnl !== 0 ? (row.pnl / Math.abs(totalPnl)) * 100 : null;
              const isNeg = row.pnl < 0;
              const isDeltaNeg = row.pnl_diff < 0;

              return (
                <Box
                  key={row.strategy_type_name}
                  onClick={() => handleStrategyClick(row.strategy_type_name)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: idx % 2 === 0 ? "background.paper" : "#f8faf9",
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    transition: "background-color 0.12s ease",
                    "&:last-child": { borderBottom: 0 },
                    "&:hover": {
                      bgcolor: "rgba(0,104,66,0.06)",
                    },
                  }}
                >
                  {/* Strategy name */}
                  <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: isNeg ? "#c53030" : "#006842",
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {row.strategy_type_name}
                    </Typography>
                  </Box>

                  {/* PnL */}
                  <Box sx={{ width: 110 }}>
                    <Tooltip title={row.pnl != null ? formatFullNumber(row.pnl) : "No data"} arrow>
                      <Box sx={{ textAlign: "right", cursor: "help" }}>
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            fontFamily: "monospace",
                            color: row.pnl == null ? "text.disabled" : isNeg ? "#c53030" : "#006842",
                          }}
                        >
                          {row.pnl != null ? formatCurrency(row.pnl) : "—"}
                        </Typography>
                        <MagnitudeBar value={row.pnl} max={maxAbsPnl} color={isNeg ? "#c53030" : "#006842"} />
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* % of Total */}
                  <Box sx={{ width: 60, textAlign: "right" }}>
                    <Typography sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "text.disabled", fontWeight: 600 }}>
                      {pctOfTotal != null ? `${pctOfTotal.toFixed(1)}%` : "—"}
                    </Typography>
                  </Box>

                  {/* PnL Delta (day-on-day) */}
                  {hasComparison && (
                    <Box sx={{ width: 110 }}>
                      <Tooltip
                        title={
                          row.pnl_diff != null
                            ? formatFullNumber(row.pnl_diff)
                            : row.only_base
                              ? "New strategy — no prior-day data"
                              : row.only_compare
                                ? "Missing today — present prior-day"
                                : "No data"
                        }
                        arrow
                      >
                        <Box sx={{ textAlign: "right", cursor: "help" }}>
                          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 0.35 }}>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                color: row.pnl_diff == null ? "text.disabled" : isDeltaNeg ? "#c53030" : "#006842",
                              }}
                            >
                              Δ
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                fontFamily: "monospace",
                                color: row.pnl_diff == null ? "text.disabled" : isDeltaNeg ? "#c53030" : "#006842",
                              }}
                            >
                              {row.pnl_diff != null
                                ? `${isDeltaNeg ? "-" : "+"}${formatCurrency(Math.abs(row.pnl_diff))}`
                                : "—"}
                            </Typography>
                          </Box>
                          <MagnitudeBar value={row.pnl_diff} max={maxAbsDelta} color={isDeltaNeg ? "#c53030" : "#006842"} />
                        </Box>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Arrow */}
                  <Box sx={{ width: 32, display: "flex", justifyContent: "center" }}>
                    <Tooltip title="View investments" arrow>
                      <ArrowForwardIcon sx={{ fontSize: 15, color: "text.disabled", transition: "color 0.12s", ".MuiBox-root:hover &": { color: "#006842" } }} />
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
